import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEditor } from '@smartfire/glue-ui/block';
import useGeolocation from '@rooks/use-geolocation';
import { mapboxConfig } from 'src/config/mapboxConfig';
import { Button } from '../../../../components/Button';
import { GetClosestStoreType } from '../Controller';
import { Map } from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import target from '../../../../assets/icons/target.svg';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import style from './style.module.scss';
import clsx from 'clsx';

interface SearchProps extends React.HTMLAttributes<HTMLDivElement> {
    map: Map | null;
    setMap?: React.Dispatch<React.SetStateAction<Map>>;
    mapRef?: React.RefObject<HTMLDivElement>;
    getClosestStore: GetClosestStoreType;
    context?: string;
}

export const Search: React.FC<SearchProps> = ({
    map,
    setMap,
    mapRef,
    getClosestStore,
    context = 'default',
    ...props
}) => {
    const { isEditing } = useEditor();
    const [when, setWhen] = useState(false);
    const geocoderRef = useRef<HTMLDivElement | null>(null);

    const geocoder = useMemo(() => {
        if (!mapboxConfig) return;
        return new MapboxGeocoder({
            accessToken: mapboxConfig.accessToken ?? '',
            placeholder: 'Saisissez une ville ou un code postal…',
            flyTo: false,
            marker: false,
            countries: 'FR',
            language: 'fr',
        });
    }, []);

    const centerAndResize = useCallback(
        (e) => {
            if (mapboxConfig && map) {
                map.setCenter(e.result.center);
                map.setZoom(mapboxConfig.zoom ?? 1);
                try {
                    map.fitBounds(map.getCenter().toBounds(getClosestStore(map.getCenter(), true).dist ?? 0), {
                        padding: 40,
                    });
                } catch (error) {
                    map.setZoom(1);
                }
            }
        },
        [map, getClosestStore],
    );

    useEffect(() => {
        if (geocoder && geocoderRef.current && map) {
            geocoder.on('result', centerAndResize);
            geocoderRef.current.textContent = '';
            geocoderRef.current.appendChild(geocoder.onAdd(map));
        }
    }, [geocoderRef, map, geocoder, centerAndResize]);

    const geoObj = useGeolocation({
        when,
    });
    useEffect(() => {
        //reinit the useGeo
        if (when) {
            setWhen(false);
        }
    }, [when, setWhen]);

    useEffect(() => {
        if (mapboxConfig && map && geoObj?.lng && geoObj?.lat) {
            map.setCenter([geoObj.lng, geoObj.lat]);
            map.setZoom(mapboxConfig.zoom ?? 1);
            try {
                map.fitBounds(map.getCenter().toBounds(getClosestStore(map.getCenter(), true).dist ?? 0), {
                    padding: 40,
                });
            } catch (e) {
                map.setZoom(1);
            }
        }
    }, [geoObj, getClosestStore, map]);

    const search = useCallback(() => {
        const inputEl: HTMLInputElement | null | undefined = geocoderRef.current?.querySelector(
            '.mapboxgl-ctrl-geocoder--input',
        );
        if (inputEl) {
            geocoder?.query(inputEl.value);
        }
    }, [geocoder, geocoderRef]);

    useEffect(() => {
        const searchButton = document.querySelector('.mapboxgl-ctrl-geocoder--icon-search');
        if (!searchButton) return;
        searchButton.addEventListener('click', search);
        return () => {
            searchButton.removeEventListener('click', search);
        };
    }, [search, map]);

    return (
        <div className={clsx(style.saerch, style[context])} {...props}>
            <div className={style.content}>
                <div className={style.geocoderContainer} ref={geocoderRef} />
                <Button
                    onClick={() => {
                        !isEditing && setWhen(true);
                    }}
                    className={style.locateButton}
                >
                    <div className={style.container}>
                        Autour de moi <img alt="me localiser" className={style.icon} src={target} />
                    </div>
                </Button>
            </div>
        </div>
    );
};
